import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';

const ITEMS_PER_PAGE = 10;

const ResourceManager = () => {
  const { isAuthenticated } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(new Set());
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [filters, setFilters] = useState({
    type: '',
    topic: '',
    date: '',
  });

  // Get API base URL
  const getApiBase = () => {
    const envBase = (process.env.REACT_APP_API_BASE || '').trim();
    if (envBase) return envBase.replace(/\/$/, '');
    
    if (typeof window !== 'undefined') {
      const saved = window.localStorage.getItem('API_BASE');
      if (saved) return String(saved).replace(/\/$/, '');
    }
    return '';
  };

  const loadFiles = async () => {
    setLoading(true);
    setError('');
    
    try {
      const apiBase = getApiBase();
      if (!apiBase) {
        throw new Error('API base URL 未配置');
      }

      const queryParams = new URLSearchParams();
      if (filters.type) queryParams.append('type', filters.type);
      if (filters.topic) queryParams.append('topic', filters.topic);
      if (filters.date) queryParams.append('date', filters.date);

      const url = `${apiBase}/files${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`載入失敗: ${response.status}`);
      }

      const data = await response.json();
      setFiles(data.files || []);
    } catch (e) {
      console.error('Failed to load files:', e);
      setError(e.message || '載入資源失敗');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadFiles();
    }
  }, [isAuthenticated, filters]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError('');
    setMessage('');

    try {
      const apiBase = getApiBase();
      if (!apiBase) {
        throw new Error('API base URL 未配置');
      }

      const formData = new FormData();
      formData.append('file', file);
      
      const topicInput = document.getElementById('upload-topic');
      if (topicInput && topicInput.value) {
        formData.append('topic', topicInput.value);
      }

      const response = await fetch(`${apiBase}/file-upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '上傳失敗');
      }

      const result = await response.json();
      setMessage(`成功上傳文件: ${result.filename}`);
      
      // Reset topic input
      if (topicInput) topicInput.value = '';
      // Reset file input
      event.target.value = '';
      
      // Reload files
      await loadFiles();
    } catch (e) {
      console.error('Upload failed:', e);
      setError(e.message || '上傳失敗');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = (file) => {
    if (file.url) {
      window.open(file.url, '_blank');
    } else {
      setError('文件URL不可用');
    }
  };

  const handleCopyUrl = async (file) => {
    // Use the S3 presigned URL from file.url
    if (!file.url) {
      setError('文件URL不可用');
      return;
    }

    const fileUrl = file.url;

    try {
      await navigator.clipboard.writeText(fileUrl);
      setMessage(`已複製文件URL: ${file.filename || file.fileId || file.sk}`);
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fileUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setMessage(`已複製文件URL: ${file.filename || file.fileId || file.sk}`);
        setTimeout(() => setMessage(''), 3000);
      } catch (fallbackErr) {
        setError('複製失敗，請手動複製');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('確定要刪除這個文件嗎？')) {
      return;
    }

    setDeleting(prev => new Set(prev).add(fileId));
    setError('');
    setMessage('');

    try {
      const apiBase = getApiBase();
      if (!apiBase) {
        throw new Error('API base URL 未配置');
      }

      const response = await fetch(`${apiBase}/files`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids: [fileId] }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '刪除失敗');
      }

      const result = await response.json();
      setMessage(result.message || '刪除成功');
      
      // Reload files
      await loadFiles();
      setSelectedFiles(new Set());
    } catch (e) {
      console.error('Delete failed:', e);
      setError(e.message || '刪除失敗');
    } finally {
      setDeleting(prev => {
        const next = new Set(prev);
        next.delete(fileId);
        return next;
      });
    }
  };

  const handleBatchDelete = async () => {
    if (selectedFiles.size === 0) {
      setError('請至少選擇一個文件');
      return;
    }

    if (!window.confirm(`確定要刪除選中的 ${selectedFiles.size} 個文件嗎？`)) {
      return;
    }

    setDeleting(prev => new Set([...prev, ...selectedFiles]));
    setError('');
    setMessage('');

    try {
      const apiBase = getApiBase();
      if (!apiBase) {
        throw new Error('API base URL 未配置');
      }

      const response = await fetch(`${apiBase}/files`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids: Array.from(selectedFiles) }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '批量刪除失敗');
      }

      const result = await response.json();
      setMessage(result.message || '批量刪除成功');
      
      // Reload files
      await loadFiles();
      setSelectedFiles(new Set());
    } catch (e) {
      console.error('Batch delete failed:', e);
      setError(e.message || '批量刪除失敗');
    } finally {
      setDeleting(new Set());
    }
  };

  const handleSelectFile = (fileId) => {
    setSelectedFiles(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedFiles.size === paginatedFiles.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(paginatedFiles.map(f => f.fileId || f.sk)));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      image: '圖片',
      pdf: 'PDF',
      word: 'Word',
      ppt: 'PPT',
      video: '視頻',
    };
    return labels[type] || type;
  };

  // Pagination
  const totalPages = Math.ceil(files.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedFiles = files.slice(startIndex, endIndex);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <section className="py-5">
      <div className="container">
        <h2 className="mb-4">資源管理</h2>

        {/* Upload Section */}
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">上傳文件</h5>
            <div className="row g-3">
              <div className="col-md-4">
                <label htmlFor="file-upload" className="form-label">
                  選擇文件
                </label>
                <input
                  type="file"
                  className="form-control"
                  id="file-upload"
                  onChange={handleUpload}
                  disabled={uploading}
                  accept="image/*,.pdf,.doc,.docx,.ppt,.pptx,video/*"
                />
              </div>
              <div className="col-md-4">
                <label htmlFor="upload-topic" className="form-label">
                  主題（可選）
                </label>
                <select
                  className="form-select"
                  id="upload-topic"
                  defaultValue=""
                >
                  <option value="">請選擇主題</option>
                  <option value="company">company</option>
                  <option value="user">user</option>
                </select>
              </div>
              <div className="col-md-4 d-flex align-items-end">
                {uploading && (
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">上傳中...</span>
                  </div>
                )}
                <small className="text-muted">
                  支持：圖片（5MB）、PDF/Word/PPT（10MB）、視頻（10MB）
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">篩選</h5>
            <div className="row g-3">
              <div className="col-md-3">
                <label htmlFor="filter-type" className="form-label">類型</label>
                <select
                  className="form-select"
                  id="filter-type"
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                >
                  <option value="">全部</option>
                  <option value="image">圖片</option>
                  <option value="pdf">PDF</option>
                  <option value="word">Word</option>
                  <option value="ppt">PPT</option>
                  <option value="video">視頻</option>
                </select>
              </div>
              <div className="col-md-3">
                <label htmlFor="filter-topic" className="form-label">主題</label>
                <input
                  type="text"
                  className="form-control"
                  id="filter-topic"
                  value={filters.topic}
                  onChange={(e) => setFilters({ ...filters, topic: e.target.value })}
                  placeholder="輸入主題"
                />
              </div>
              <div className="col-md-3">
                <label htmlFor="filter-date" className="form-label">日期</label>
                <input
                  type="date"
                  className="form-control"
                  id="filter-date"
                  value={filters.date}
                  onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                />
              </div>
              <div className="col-md-3 d-flex align-items-end">
                <button
                  className="btn btn-outline-secondary w-100"
                  onClick={() => setFilters({ type: '', topic: '', date: '' })}
                >
                  清除篩選
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError('')}
              aria-label="Close"
            />
          </div>
        )}
        {message && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            {message}
            <button
              type="button"
              className="btn-close"
              onClick={() => setMessage('')}
              aria-label="Close"
            />
          </div>
        )}

        {/* Files Table */}
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="card-title mb-0">
                文件列表 ({files.length})
              </h5>
              {selectedFiles.size > 0 && (
                <button
                  className="btn btn-danger btn-sm"
                  onClick={handleBatchDelete}
                  disabled={deleting.size > 0}
                >
                  批量刪除 ({selectedFiles.size})
                </button>
              )}
            </div>

            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">載入中...</span>
                </div>
              </div>
            ) : paginatedFiles.length === 0 ? (
              <div className="text-center text-muted py-4">
                沒有找到文件
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered table-hover">
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}>
                          <input
                            type="checkbox"
                            checked={selectedFiles.size === paginatedFiles.length && paginatedFiles.length > 0}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th>文件名</th>
                        <th style={{ width: '100px' }}>類型</th>
                        <th style={{ width: '100px' }}>大小</th>
                        <th style={{ width: '150px' }}>主題</th>
                        <th style={{ width: '150px' }}>上傳時間</th>
                        <th style={{ width: '180px' }}>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedFiles.map((file) => {
                        const fileId = file.fileId || file.sk;
                        const isDeleting = deleting.has(fileId);
                        const isSelected = selectedFiles.has(fileId);
                        
                        return (
                          <tr key={fileId}>
                            <td>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleSelectFile(fileId)}
                              />
                            </td>
                            <td>
                              {file.filename ? (
                                <button
                                  className="btn btn-link p-0 text-start text-decoration-none"
                                  onClick={() => handleCopyUrl(file)}
                                  style={{ 
                                    color: '#0d6efd',
                                    cursor: 'pointer',
                                    border: 'none',
                                    background: 'none',
                                    textDecoration: 'underline'
                                  }}
                                  title="點擊複製文件URL"
                                >
                                  {file.filename}
                                </button>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td>{getTypeLabel(file.type)}</td>
                            <td>{formatFileSize(file.size)}</td>
                            <td>{file.topic || '-'}</td>
                            <td>{formatDate(file.createdAt)}</td>
                            <td>
                              <div className="d-flex gap-1">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleDownload(file)}
                                  title="下載"
                                >
                                  <i className="fas fa-download"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(fileId)}
                                  disabled={isDeleting}
                                  title="刪除"
                                >
                                  {isDeleting ? (
                                    <span className="spinner-border spinner-border-sm" role="status">
                                      <span className="visually-hidden">刪除中...</span>
                                    </span>
                                  ) : (
                                    <i className="fas fa-trash"></i>
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <nav aria-label="文件分頁" className="mt-3">
                    <ul className="pagination pagination-sm justify-content-center">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          上一頁
                        </button>
                      </li>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <li
                          key={page}
                          className={`page-item ${currentPage === page ? 'active' : ''}`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          下一頁
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResourceManager;

